import { useEffect, useRef, useState } from 'react';
import { Maximize, MicOff, Minimize, Play } from 'lucide-react';
import { AvatarCircle, cn } from '@jeraisonne/ui';

/** iOS Safari n'implémente PAS la Fullscreen API standard sur un conteneur
 * générique (`<div>.requestFullscreen`) — seul un `<video>` peut aller en
 * plein écran nativement, via cette méthode WebKit dédiée. */
type IOSVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
};

export function VideoTile({
  stream,
  label,
  muted,
  mirrored,
  screenShare,
  className,
  onActivate,
  activateLabel,
  cameraOn = true,
  micOn = true,
  speaking = false,
  badge,
}: {
  stream: MediaStream | null;
  label: string;
  muted?: boolean;
  mirrored?: boolean;
  /** Le flux affiché est un partage d'écran — jamais rogné (`object-contain`), l'intégralité du display doit rester visible. */
  screenShare?: boolean;
  className?: string;
  /** Rend toute la tuile cliquable (ex. agrandissement "spotlight" dans la page) — sans ça la tuile n'est pas interactive. */
  onActivate?: () => void;
  /** Libellé accessible du clic sur la tuile — requis dès que `onActivate` est fourni. */
  activateLabel?: string;
  /** Caméra éteinte (ou absente) : un AVATAR remplace l'image vidéo — jamais un rectangle noir muet.
   * Le `<video>` reste monté (son piste AUDIO, elle, continue de jouer). */
  cameraOn?: boolean;
  /** Affiche une icône micro coupé dans le coin — indicatif (annoncé par le pair), pas une preuve. */
  micOn?: boolean;
  /** Ce participant parle actuellement (détection locale de niveau audio) — liseré de mise en avant. */
  speaking?: boolean;
  /** Repère visuel court dans le coin opposé au nom (ex. « Hôte »). */
  badge?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Certains navigateurs (Safari/iOS en tête) bloquent parfois l'autoplay
  // d'un <video> non coupé même quand sa source est un MediaStream WebRTC —
  // sans ce filet, la tuile reste NOIRE indéfiniment (srcObject posé mais
  // aucune frame jamais décodée) sans le moindre message.
  const [needsTapToPlay, setNeedsTapToPlay] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    el.srcObject = stream;
    setNeedsTapToPlay(false);
    if (!stream) return;
    const playPromise = el.play();
    if (playPromise) {
      playPromise.catch((err: unknown) => {
        // Un changement de flux/démontage pendant que play() était en vol
        // (ex. bascule du spotlight au clic) interrompt la promesse avec
        // AbortError — ce n'est PAS un blocage d'autoplay, pas de bouton.
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
        console.warn(`[video-tile:${label}] play() bloqué par le navigateur`, err);
        setNeedsTapToPlay(true);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [stream, label]);

  useEffect(() => {
    function handleFullscreenChange() {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  function handleTapToPlay() {
    void ref.current
      ?.play()
      .then(() => setNeedsTapToPlay(false))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.error(`[video-tile:${label}] play() a encore échoué`, err);
      });
  }

  function handleToggleFullscreen(event: React.MouseEvent) {
    event.stopPropagation();
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }
    const container = containerRef.current;
    if (container?.requestFullscreen) {
      container.requestFullscreen().catch((err: unknown) => {
        console.warn(`[video-tile:${label}] requestFullscreen a échoué`, err);
      });
      return;
    }
    const iosVideo = ref.current as IOSVideoElement | null;
    iosVideo?.webkitEnterFullscreen?.();
  }

  return (
    <div
      ref={containerRef}
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
      onClick={onActivate}
      onKeyDown={
        onActivate
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onActivate();
              }
            }
          : undefined
      }
      aria-label={onActivate ? activateLabel : undefined}
      className={cn(
        'bg-ink-900 relative aspect-video w-full overflow-hidden rounded-2xl',
        onActivate && 'cursor-pointer',
        isFullscreen && 'aspect-auto',
        speaking && 'ring-primary-400 ring-2',
        className,
      )}
    >
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={muted}
        onLoadedMetadata={() => console.log(`[video-tile:${label}] loadedmetadata`)}
        onPlaying={() => console.log(`[video-tile:${label}] playing`)}
        onStalled={() => console.warn(`[video-tile:${label}] stalled`)}
        className={cn(
          'h-full w-full',
          screenShare ? 'object-contain' : 'object-cover',
          mirrored && 'scale-x-[-1]',
        )}
      />
      {!cameraOn && (
        <div className="bg-ink-900 absolute inset-0 flex flex-col items-center justify-center gap-2">
          <AvatarCircle name={label} size="lg" />
        </div>
      )}
      {!micOn && (
        <span
          className="bg-ink-900/70 absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded-full text-white"
          aria-label={`Micro coupé : ${label}`}
        >
          <MicOff className="size-3.5" />
        </span>
      )}
      {badge && (
        <span className="bg-primary-500 absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white">
          {badge}
        </span>
      )}
      {needsTapToPlay && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handleTapToPlay();
          }}
          aria-label={`Activer la vidéo de ${label}`}
          className="bg-ink-900/70 absolute inset-0 flex flex-col items-center justify-center gap-2 text-white"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20">
            <Play className="h-5 w-5" />
          </span>
          <span className="text-xs font-medium">Appuie pour afficher la vidéo</span>
        </button>
      )}
      {stream && (
        <button
          type="button"
          onClick={handleToggleFullscreen}
          aria-label={
            isFullscreen ? `Quitter le plein écran de ${label}` : `Plein écran : ${label}`
          }
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70 active:scale-95"
        >
          {isFullscreen ? <Minimize className="size-4" /> : <Maximize className="size-4" />}
        </button>
      )}
      <span className="absolute bottom-2 left-2 rounded-full bg-black/50 px-2 py-1 text-xs text-white">
        {label}
      </span>
    </div>
  );
}
